import { dayKey } from '$features/forecast';
import type { MacroSession } from '$domain/types';
import type { DayActivity, GoalsSnapshot, StreakInfo } from './types';

export function previousDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() - 1);
  return dayKey(dt);
}

export function dateKeyFromIso(iso: string): string {
  return dayKey(new Date(iso));
}

export function sessionDayKey(s: MacroSession): string {
  return dateKeyFromIso(s.startedAt);
}

export function uniqueActiveDays(sessions: MacroSession[]): Set<string> {
  const set = new Set<string>();
  for (const s of sessions) {
    if (s.status !== 'finished') continue;
    if (!s.endedAt) continue;
    set.add(sessionDayKey(s));
  }
  return set;
}

export function computeStreak(
  sessions: MacroSession[],
  today: string = dayKey(new Date())
): StreakInfo {
  const active = uniqueActiveDays(sessions);
  const yesterday = previousDay(today);

  const todayHasActivity = active.has(today);
  const yesterdayHasActivity = active.has(yesterday);

  let startCursor: string | null = null;
  let inGrace = false;

  if (todayHasActivity) {
    startCursor = today;
  } else if (yesterdayHasActivity) {
    startCursor = yesterday;
    inGrace = true;
  }

  let current = 0;
  if (startCursor) {
    let cursor = startCursor;
    while (active.has(cursor)) {
      current += 1;
      cursor = previousDay(cursor);
    }
  }

  // Longest streak: sort all active days, walk forward
  const sorted = Array.from(active).sort();
  let longest = 0;
  let run = 0;
  let prev: string | null = null;
  for (const d of sorted) {
    if (prev === null) {
      run = 1;
    } else if (previousDay(d) === prev) {
      run += 1;
    } else {
      run = 1;
    }
    if (run > longest) longest = run;
    prev = d;
  }

  return {
    current,
    longest,
    todayHasActivity,
    yesterdayHasActivity,
    inGrace
  };
}

export function minutesOnDay(sessions: MacroSession[], dateKey: string): number {
  let ms = 0;
  for (const s of sessions) {
    if (s.status !== 'finished') continue;
    if (sessionDayKey(s) !== dateKey) continue;
    ms += s.focusDurationMs;
  }
  return Math.round(ms / 60000);
}

export function startOfWeekKey(today: string): string {
  const [y, m, d] = today.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const dow = dt.getDay(); // 0=Sun
  const offset = dow === 0 ? 6 : dow - 1; // Monday start
  dt.setDate(dt.getDate() - offset);
  return dayKey(dt);
}

export function minutesInWeek(sessions: MacroSession[], today: string): number {
  const start = startOfWeekKey(today);
  let total = 0;
  for (const s of sessions) {
    if (s.status !== 'finished') continue;
    const k = sessionDayKey(s);
    if (k >= start && k <= today) total += s.focusDurationMs;
  }
  return Math.round(total / 60000);
}

export function buildSnapshot(
  sessions: MacroSession[],
  today: string,
  dailyTarget: number,
  weeklyTarget: number
): GoalsSnapshot {
  const todayMinutes = minutesOnDay(sessions, today);
  const weekMinutes = minutesInWeek(sessions, today);
  return {
    todayMinutes,
    weekMinutes,
    dailyTarget,
    weeklyTarget,
    dailyProgress: dailyTarget > 0 ? Math.min(1, todayMinutes / dailyTarget) : 0,
    weeklyProgress: weeklyTarget > 0 ? Math.min(1, weekMinutes / weeklyTarget) : 0
  };
}

export function lastDaysActivity(
  sessions: MacroSession[],
  today: string,
  days: number,
  activeDays: number[]
): DayActivity[] {
  const out: DayActivity[] = [];
  const [y, m, d] = today.split('-').map(Number);
  const anchor = new Date(y!, (m ?? 1) - 1, d ?? 1);
  for (let i = days - 1; i >= 0; i--) {
    const dt = new Date(anchor);
    dt.setDate(dt.getDate() - i);
    const key = dayKey(dt);
    const mins = minutesOnDay(sessions, key);
    const sessionCount = sessions.filter(
      (s) => s.status === 'finished' && sessionDayKey(s) === key
    ).length;
    out.push({
      date: key,
      minutes: mins,
      sessions: sessionCount,
      isActiveDay: activeDays.includes(dt.getDay())
    });
  }
  return out;
}